import type { InstantFramePatch, InterpolatedFramePatch, SpaceDerivationFrame, TransitionPhase } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { deriveWorkflowStateEntryPropagation } from '@/workflow/_pure/derivation/workflowStateEntryPropagation';
import { parseWorkflowStateEntryItemKey } from '@/workflow/_pure/keyCodec';
import type { Config } from '@/workflow-runner/_contract/config';

export function derivePanelInputPotentialPropagationPhases(args: {
    frame: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    sources: NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['entityItemByKey']>;
    config: Config;
    color: string;
    transitionKey: string;
    previouslyMaterialized: ReadonlySet<string>;
}): { phases: TransitionPhase[]; materializedKeys: string[] } {
    const links = args.frame.itemCollections.flatMap((collection) => collection.itemCollectionKind === 'connector'
        ? collection.items.flatMap((item) => {
            const { from, to } = item.endpoints;
            if (from.endpointKind !== 'static' || to.endpointKind !== 'static') return [];
            if (parseWorkflowStateEntryItemKey(from.itemKey)?.direction !== 'input'
                || !parseWorkflowStateEntryItemKey(to.itemKey)) return [];
            return [{ key: item.itemKey, source: to.itemKey, target: from.itemKey }];
        }) : []);
    type EntityPatch = NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['entityItemByKey']>;
    const sources = new Map<string, NonNullable<EntityPatch[keyof EntityPatch]>>();
    for (const [key, source] of Object.entries(args.sources)) {
        if (source?.shape && 'appearanceMode' in source.shape && source.shape.appearanceMode === 'filled') {
            sources.set(key, source);
        }
    }
    const hops = deriveWorkflowStateEntryPropagation({
        dependencies: links,
        sources,
        previouslyMaterialized: new Set([...args.previouslyMaterialized, ...Object.keys(args.sources)]),
        resolveTarget: (link, source): NonNullable<EntityPatch[keyof EntityPatch]> | undefined => {
            const key = link.target as `item:entity:${string}`;
            const targetState = args.target.itemParametricState?.entityItemByKey?.[key];
            const baseState = args.frame.itemParametricState?.entityItemByKey?.[key];
            if (baseState?.shape?.shapeKind !== 'panel') return undefined;
            // Structural renames can precede the target frame's entry keys.
            const label = targetState?.shape?.shapeKind === 'panel' && targetState.shape.appearanceMode === 'filled'
                ? targetState.label : source.label;
            if (baseState.shape.appearanceMode === 'filled' && JSON.stringify(baseState.label) === JSON.stringify(label)) return undefined;
            return { shape: { ...baseState.shape, appearanceMode: 'filled' }, ...(label === undefined ? {} : { label }) };
        },
    });
    const phases: TransitionPhase[] = [];
    const materializedKeys: string[] = [];
    for (const [hopIndex, hop] of hops.entries()) {
        const next: NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['entityItemByKey']> = {};
        const start: NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['connectorItemByKey']> = {};
        const end: NonNullable<NonNullable<InterpolatedFramePatch['itemParametricState']>['connectorItemByKey']> = {};
        const clear: typeof start = {};
        for (const { dependency: link, value } of hop) {
            const key = link.target as `item:entity:${string}`;
            const connectorKey = link.key as `item:connector:${string}`;
            materializedKeys.push(link.target);
            next[key] = value;
            start[connectorKey] = { progressiveActivation: { progress01: 0, direction: 'to-from', color: args.color } };
            end[connectorKey] = { progressiveActivation: { progress01: 1 } };
            clear[connectorKey] = { progressiveActivation: null };
        }
        const key = `${args.transitionKey}:input-potential:${hopIndex}`;
        const enabled = args.config.adapterModeConfig.playback.resourceTypeRadialRipple.enabled;
        if (enabled) phases.push(
            { transitionOptions: { transitionKind: 'instant', transitionKey: `${key}:start` },
                patch: { itemParametricState: { connectorItemByKey: start } } },
            { transitionOptions: { transitionKind: 'interpolated', transitionKey: `${key}:travel`,
                duration: args.config.adapterModeConfig.playback.inputPotentialPropagationDurationMs, easing: 'linear' },
                patch: { itemParametricState: { connectorItemByKey: end } } },
        );
        phases.push({ transitionOptions: { transitionKind: 'instant', transitionKey: `${key}:materialize` },
            patch: { itemParametricState: { entityItemByKey: next } } });
        if (enabled) phases.push({ transitionOptions: { transitionKind: 'instant', transitionKey: `${key}:clear` },
            patch: { itemParametricState: { connectorItemByKey: clear } } });
    }
    return { phases, materializedKeys };
}
